import { createFileRoute } from "@tanstack/react-router";
import { ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/nfc-hero-tap.webp";
import materialsImg from "@/assets/nfc-materials-fan.webp";
import teamImg from "@/assets/nfc-team-cards.webp";

const IMG: Record<string, string> = { hero: heroImg, materials: materialsImg, team: teamImg };

export const Route = createFileRoute("/vi/platform/nfc")({
  head: () => ({
    meta: [
      { title: "Thẻ danh thiếp NFC — banana.cards" },
      { name: "description", content: "Thẻ NFC cao cấp, bền lâu. Chạm để chia sẻ hồ sơ — không cần app." },
      { property: "og:title", content: "Thẻ danh thiếp NFC — banana.cards" },
      { property: "og:description", content: "Thẻ NFC cao cấp, bền lâu. Chạm để chia sẻ hồ sơ — không cần app." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/nfc", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Thẻ NFC"
      title="Chạm."
      italic="Chia sẻ. Xong."
      description="Thẻ NFC cao cấp bằng kim loại, đen mờ, trắng hoặc thiết kế tùy chỉnh. Ghép với hồ sơ banana và chia sẻ chỉ một lần chạm — không cần cài app phía người nhận."
      heroImage={IMG['hero']}
      metrics={[{ v: "12M+", l: "lần chạm" }, { v: "2 năm", l: "không cần pin" }, { v: "ISO/IEC", l: "tuân thủ" }]}
      bullets={[
        { icon: Smartphone, title: "Hoạt động trên mọi điện thoại", desc: "Tương thích iOS 14+ và Android 10+ — không cần cài app." },
        { icon: ShieldCheck, title: "Bền bỉ", desc: "Kim loại & PVC thiết kế cho dùng hàng ngày. Bảo hành trọn đời." },
        { icon: Sparkles, title: "Tùy biến hoàn toàn", desc: "Khắc, in màu, hình dạng tùy chỉnh — và thương hiệu đội ở quy mô lớn." }
      ]}
      deepDives={[
        { eyebrow: "Chất liệu", title: "Kim loại cao cấp, đen mờ,", italic: "hoặc thiết kế của bạn.", description: "Chọn từ bộ sưu tập bề mặt hoặc tải thiết kế riêng. Mỗi thẻ được khắc laser hoặc in màu đầy đủ và kiểm tra chất lượng thủ công.", image: IMG["materials"], reverse: false, points: ["Thép chải, đen mờ, trắng cổ điển", "In màu đầy đủ hoặc khắc laser", "Tùy chọn gỗ & tre thân thiện môi trường", "Hình dạng tùy chỉnh cho đội"] },
        { eyebrow: "Cho đội", title: "Cấp, quản lý, thu hồi.", italic: "Tất cả một nơi.", description: "Giao thẻ NFC có thương hiệu cho cả đội trong vài ngày. Cấp hồ sơ, cập nhật từ xa và thu hồi truy cập khi ai đó rời đi — không chạm vào phần cứng.", image: IMG["team"], reverse: true, points: ["Đặt số lượng lớn & giao toàn cầu", "Gán hồ sơ tập trung", "Ghi lại và thu hồi từ xa", "Phân tích và định tuyến lead theo thẻ"] }
      ]}
      howItWorks={[
        { title: "Đặt thẻ", desc: "Chọn bề mặt và tải thiết kế." },
        { title: "Chúng tôi lập trình & gửi", desc: "Thẻ đến đã liên kết sẵn với hồ sơ." },
        { title: "Chạm để chia sẻ", desc: "Đưa vào điện thoại để chia sẻ tức thì." },
        { title: "Cập nhật bất cứ lúc nào", desc: "Ghi lại thẻ hoặc đổi hồ sơ liên kết từ app." }
      ]}
      capabilities={[
        "Chip NFC ghi lại được",
        "Kim loại, đen, trắng, tùy chỉnh",
        "Đặt số lượng lớn và cấp cho đội",
        "Miễn phí vận chuyển toàn cầu trên $99",
        "Khắc và in màu",
        "Bảo hành phần cứng trọn đời",
        "Hoạt động không cần app",
        "Theo dõi thân thiện GDPR"
      ]}
      faq={[
        { q: "Thẻ NFC Banana hoạt động thực tế như thế nào?", a: "Mỗi thẻ Banana có chip NFC bên trong. Đưa thẻ vào mặt sau bất kỳ smartphone hiện đại nào — thông báo bật lên, người nhận chạm vào và hồ sơ của bạn mở trong trình duyệt mặc định. Không app, không Bluetooth, không ghép cặp, không phiền hà. Toàn bộ trao đổi mất khoảng hai giây." },
        { q: "Có hoạt động trên iPhone và Android không?", a: "Có. Đọc NFC có sẵn trên mọi iPhone từ XS trở lên (iOS 14+) và gần như mọi Android trong thập kỷ qua. Trên iPhone hiện đại, hoạt động không cần mở app — chỉ chạm đầu điện thoại vào thẻ." },
        { q: "Tôi có thể lập trình lại thẻ để trỏ nơi khác không?", a: "Có — thẻ NFC liên kết với hồ sơ Banana, nên cập nhật hồ sơ cập nhật mọi lần chạm. Bạn cũng có thể chuyển thẻ nào đang \"active\" trên NFC, hoặc tạm thời trỏ NFC đến URL tùy chỉnh (trang landing chiến dịch, RSVP sự kiện, v.v.) mà không cần in lại." },
        { q: "Thẻ NFC Banana làm từ chất liệu gì?", a: "Kim loại cao cấp, kim loại chải, vàng 24kt, tre, PVC đen và PVC trắng — chọn bề mặt khớp thương hiệu. Thiết kế in màu đầy đủ, cạnh-tới-cạnh, với tùy chọn khắc QR để thẻ hoạt động cả trên điện thoại tắt NFC." },
        { q: "Có giao quốc tế không?", a: "Có — chúng tôi giao đến 60+ quốc gia với giao có tracking. Đặt số lượng lớn cho đội bao gồm miễn phí xem thiết kế và tùy chọn cá nhân hóa từng thẻ (tên và chức danh từng người)." },
        { q: "Có giá sỉ cho đội không?", a: "Có — giá khối lượng tự kích hoạt từ 10+ thẻ, giảm thêm ở 50, 100 và 500. Liên hệ cho triển khai doanh nghiệp (1.000+ thẻ) để có bề mặt tùy chỉnh và onboarding riêng." }
      ]}
      outro={ { title: "Một lần chạm.", italic: "Vô vàn kết nối.", description: "Thẻ NFC cao cấp từ $29 — tùy biến đầy đủ cho đội." } }
    />
  );
}